import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMediaPredicate } from 'react-media-hook'

import { Props as TabProps } from './Tab'

const STab = styled.div<{ active?: boolean; hidden: boolean }>`
    display: ${({ hidden }) => (hidden ? 'none' : 'flex')};
    justify-content: space-between;
    align-items: center;
    margin-bottom: -1px;
    padding: 15px;
    cursor: ${(props) => (props.active ? 'default' : 'pointer')};
    border: 1px solid #e6e6e6;
    border-bottom: none;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    font-size: 18px;
    font-style: normal;
    font-weight: var(--font-weight-bold);
    line-height: 25px;
    letter-spacing: 0em;
    text-align: left;

    @media (max-width: 900px) {
        flex-grow: 1;
        justify-content: center;
    }

    :not(:first-child) {
        margin-left: -1px;
    }

    ${(props) =>
        props.active
            ? css`
                  background-color: white;
                  border-color: #ccc;

                  + ${STab} {
                      border-left-width: 0;
                      margin-left: 0;
                  }
              `
            : css`
                  color: ${({ theme }) => theme.brandColors.CA_Blue};
              `}
`

const STabBody = styled.div`
    border: 1px solid #ccc;
    border-radius: 8px;
    border-top-left-radius: 0;
    padding: 40px 20px;

    @media (max-width: 900px) {
        border-top-right-radius: 0;
    }
`

const SIcon = styled(FontAwesomeIcon)`
    font-size: 17px;

    @media (min-width: 900px) {
        margin-right: 10px;
    }
`

export const SLabel = styled.span`
    margin-top: 3px;
`

const STabsContainer = styled.div`
    display: flex;

    @media (max-width: 900px) {
        padding: 0 17px;
    }
`

export type Props = {
    children: Array<React.ReactElement<TabProps>>
    active?: string
    onTabClick?: (label: string) => void
}

const Tabs = ({ children, active }: Props): React.ReactElement => {
    const [activeTab, setActiveTab] = useState(() => {
        if (!children || !children.length) return
        return (children.find((child) => child.props.label === active) || children[0]).props.label
    })

    return (
        <div>
            <div>
                {children.map(({ props: { label, icon } }) => (
                    <STab key={label} active={activeTab === label} onClick={() => setActiveTab(label)}>
                        {icon && <SIcon icon={icon} size="lg" />}
                        {label}
                    </STab>
                ))}
            </div>
            <STabBody>{children.filter((child) => child.props.label === activeTab)}</STabBody>
        </div>
    )
}

/**
 * Like normals Tabs but uses CSS to hide invisible blocks so all tabs are rendered and don't unmount when hidden
 */
export const PersistentTabs = ({ children, active, onTabClick }: Props): React.ReactElement => {
    useEffect(() => {
        if (children.find((tabs) => tabs.props.label === activeTab && tabs.props.hidden)) {
            setActiveTab(children[0].props.label)
        }
    }, [children])

    const [activeTab, setActiveTab] = useState(() => {
        if (!children || !children.length) return
        return (children.find((child) => child.props.label === active) || children[0]).props.label
    })

    const isDesktopScreen = useMediaPredicate('(min-width: 900px)')

    return (
        <div>
            <STabsContainer>
                {children.map(({ props: { label, icon, hidden } }) => (
                    <STab
                        key={label}
                        active={activeTab === label}
                        onClick={() => {
                            setActiveTab(label)
                            onTabClick && onTabClick(label)
                        }}
                        hidden={hidden}
                    >
                        {icon && <SIcon icon={icon} size="lg" data-testid={'icon-' + label} />}
                        {isDesktopScreen && <SLabel data-testid={label}>{label}</SLabel>}
                    </STab>
                ))}
            </STabsContainer>
            <STabBody>
                {children.map((child) => {
                    if (!React.isValidElement(child)) return child
                    return React.cloneElement(child, {
                        key: child.props.label,
                        style: {
                            display: child.props.label === activeTab ? 'block' : 'none',
                        },
                    })
                })}
            </STabBody>
        </div>
    )
}

export default Tabs
